import { Title } from '@solidjs/meta';
import {
	Button,
	Errors,
	Field,
	Heading,
	Input,
	Label,
	Option,
	Select,
	TextField,
	Container,
	Page,
	Section,
	Grid,
	GridFive,
	GridSeven,
} from '@troon/ui';
import { Formspree, formspreeAction } from '@troon/forms';
import { createEffect, Match, Show, Switch } from 'solid-js';
import { useSubmission } from '@solidjs/router';
import { IconCircleCheckCustom } from '@troon/icons/circle-check-custom';
import { useTrackEvent } from '@troon/analytics';
import { getConfigValue } from '../modules/config';
import { Hero } from '../components/hero/photo';
import { useUser } from '../providers/user';
import type { RouteDefinition } from '@solidjs/router';

export default function ContactUsPage() {
	const submission = useSubmission(action);
	const user = useUser();

	const trackEvent = useTrackEvent();
	createEffect(() => {
		if (submission.result) {
			trackEvent('formSubmission', { form: 'contact-us', formSuccess: true });
		} else if (submission.error) {
			trackEvent('formSubmission', { form: 'contact-us', formSuccess: false });
		}
	});

	return (
		<>
			<Title>Contact Us | Access</Title>

			<Hero src={`${getConfigValue('IMAGE_HOST')}/hero/contact.jpg`} crop="center">
				<Heading as="h1" class="text-center text-white">
					Contact Us
				</Heading>
			</Hero>

			<Container>
				<Page>
					<Grid>
						<GridFive>
							<Section>
								<Heading as="h2">Have a question?</Heading>
								<p>
									If you have any questions or need help with something, please feel free to reach out to us using the
									contact form.
								</p>
							</Section>
						</GridFive>
						<GridSeven class="rounded-lg border border-neutral bg-neutral-100 p-4 md:p-8">
							<Switch>
								<Match when={submission.result}>
									<div class="container mx-auto flex max-w-xl flex-col items-center gap-6 text-center">
										<IconCircleCheckCustom class="text-8xl" />
										<Heading as="h1" size="h2">
											Message received!
										</Heading>
										<p class="text-lg">
											Thanks for contacting us! Our team is reviewing your request and we will get in touch with you
											shortly.
										</p>
									</div>
								</Match>
								<Match when>
									<Formspree action={action}>
										<Field name="Reason for contact" required>
											<Label>Reason for contact</Label>
											<Select>
												<Option disabled selected class="hidden" />
												<Show when={import.meta.env.DEV}>
													<Option>DEV-ONLY Test submission</Option>
												</Show>
												<Option value="Troon Access">Access Premium</Option>
												<Option>Website Related</Option>
												<Option>Estore/Sales</Option>
												<Option>Other</Option>
											</Select>
										</Field>

										<input type="hidden" name="Troon Rewards Number" value={user()?.troonRewardsId} />

										<TextField name="Name" required>
											<Label>First and last name</Label>
											<Input
												autocomplete="name"
												value={user() ? `${user()?.firstName} ${user()?.lastName}` : undefined}
											/>
										</TextField>

										<div class="flex w-full justify-stretch gap-4">
											<TextField name="email" required class="grow">
												<Label>Email address</Label>
												<Input autocomplete="email" inputMode="email" value={user()?.email} />
											</TextField>

											<TextField name="Phone number" class="grow">
												<Label>Phone number</Label>
												<Input autocomplete="tel" inputMode="tel" value={user()?.phoneNumber ?? undefined} />
											</TextField>
										</div>

										<TextField name="Street address">
											<Label>Street address</Label>
											<Input autocomplete="street-address" />
										</TextField>

										<div class="flex w-full justify-stretch gap-4">
											<TextField name="City" class="grow">
												<Label>City</Label>
												<Input autocomplete="address-level2" />
											</TextField>
											<TextField name="State" class="grow">
												<Label>State</Label>
												<Input autocomplete="address-level1" />
											</TextField>
											<TextField name="Postal Code" class="grow">
												<Label>Postal Code</Label>
												<Input autocomplete="postal-code" />
											</TextField>
										</div>

										<TextField name="Supplemental information" required>
											<Label>Supplemental information</Label>
											<Input multiline />
										</TextField>

										<Errors />
										<Button type="submit" class="w-fit" data-scan-exclude="true">
											Submit
										</Button>
									</Formspree>
								</Match>
							</Switch>
						</GridSeven>
					</Grid>
				</Page>
			</Container>
		</>
	);
}

const action = formspreeAction(getConfigValue('FORMSPREE_PROJECT_ID'), 'contact-us');
export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
